import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { updateJob } from "@endpoints/job.service";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { RootState } from "@store/reducers";
import { getHiringCompanyWithJobCount } from "@endpoints/recruiter.service";
import AnimatedNumber from "@components/AnimatedNumber";
import { NavLink } from "react-router-dom";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { SearchIcon } from "lucide-react";

const CompanyRowCard = ({ data }) => {
    return (
        <NavLink
            to={`/job/list-by-company/${data?.hiringCompany?._id}`}
            className="grid grid-cols-[0.5fr_2fr_0.5fr] gap-28 items-center justify-between bg-white border-b border-[#ececec] rounded px-4 py-5 hover:bg-gray-50 cursor-pointer"
        >
            <div className="flex flex-col gap-2.5 pt-1 h-full">
                <span className="font-medium text-light-black text-xs">{data?.hiringCompany?.refNo}</span>
                <span className="font-bold text-primary text-xs">{data?.hiringCompany?.companyName}</span>
            </div>
            <div className="flex flex-col gap-1">
                <span className="font-medium text-xs italic text-light-black">{dayjs(data?.latestTracker?.tracker?.createdAt).format('hh:mm a DD MMM YY')}</span>
                <div className="flex flex-col justify-center border rounded-md p-3 h-[3.8rem]">
                    <span className="text-xs text-light-black font-medium">Latest Activity : <span className="font-bold text-primary">{data?.latestTracker?.tracker?.status || 'N/A'}</span></span>
                </div>
            </div>
            <div className="flex flex-col items-center gap-2">
                <span className="text-xs font-medium text-light-black">Job Posts</span>
                <div className="h-[3.8rem] w-[3.8rem] rounded-full border-2 flex">
                    <AnimatedNumber
                        value={data?.totalJobs || 0}
                        className="font-bold text-sm text-primary m-auto"
                    />
                </div>
            </div>
        </NavLink>
    );
};

const LIMIT = 25
const PAGE_LIMIT = 5

const ViewByHiringCompany = () => {
    const hasFetched = useRef(false);
    const { enqueueSnackbar } = useSnackbar();
    const {
        control,
        formState: { errors },
        watch,
    } = useForm({
        defaultValues: {
            search: undefined,
            searchBy: "Search by",
        },
    });
    const fields = watch();

    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [hiringCompanies, setHiringCompanies] = useState([])
    const [page, setPage] = useState(1)


    async function fetchHiringCompanies() {
        try {
            if (page > 1) {
                setIsLoadingMore(true)
            } else {
                setIsLoading(true)
            }
            const { success, data } = await getHiringCompanyWithJobCount({ limit: 100, page: 1 })
            if (success) {
                setHiringCompanies(data)
            }
        } catch (error) {
            enqueueSnackbar(error, { variant: 'error' })
        } finally {
            setIsLoading(false)
            setIsLoadingMore(false)
        }
    }

    function handleLoadMore() {
        if (page >= PAGE_LIMIT) return
        setPage((prev) => prev + 1)
    }


    // const fetchHiringCompanies2 = () => {
    //     getHiringCompanyWithJobCount({ limit: 100, page: 1 })
    //         .then((response) => {
    //             const { data, success } = response;
    //             if (success) {
    //                 setHiringCompanies(data)
    //             }
    //         })
    //         .finally(() => setIsLoading(false));
    // };

    const handleCloseJob = async (jobId, status) => {
        try {
            const { success } = await updateJob({ jobId, status });
            if (success) {
                fetchHiringCompanies();
                enqueueSnackbar("Job Closed Successfully!", { variant: "success" });
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            fetchHiringCompanies();
        }
    }, [fields?.search]);

    return (
        <div className="min-h-0 flex flex-col flex-1">
            {/* Search container */}
            <div className="flex items-center w-max my-3.5 border rounded-md bg-gray-50 px-3">
                <input
                    className="w-64 text-xs font-medium bg-transparent outline-none h-9"
                    placeholder="Search..."
                />
                <SearchIcon color="gray" size={16}/>
            </div>
            {/* List section  */}
            {
                isLoading ?
                    <div className="flex flex-1 justify-center items-center">
                        <LoadingAnimation color="black" />
                    </div>
                    :
                    !hiringCompanies?.length ?
                        <span className="m-auto font-medium text-sm text-light-black">No hiring companies available.</span>
                        :
                        <div className="flex flex-col min-h-0 overflow-y-scroll no-scrollbar border-t">
                            {hiringCompanies?.map((item) => {
                                return (
                                    <CompanyRowCard
                                        data={item}
                                    />
                                );
                            })}
                        </div>
            }
        </div>
    );

};

export default ViewByHiringCompany;
